import {
  ApplicationError,
  type ApplicationErrorCode,
  type ApplicationErrorLike,
  type ApplicationErrorResponse,
  toApplicationErrorResponse,
} from './applicationError';

export type ExpectedAuthenticationErrorReason =
  | 'missing_token'
  | 'invalid_authorization_header'
  | 'invalid_csrf_token'
  | 'invalid_token'
  | 'expired_token'
  | 'revoked_token'
  | 'invalidated_session'
  | 'idle_session_expired'
  | 'unverified_email'
  | 'account_not_found'
  | 'no_accessible_projects'
  | 'saml_entry_denied';

const UnauthorizedMessage = '有効なログイン情報が見つかりませんでした';
const ForbiddenMessage = 'アクセス権限がありません';
const EmailNotVerifiedMessage = 'メールアドレスが確認されていません';
const NoAccessibleProjectsMessage = 'アクセス可能なプロジェクトがありません';
const SamlEntryDeniedMessage = 'SAMLログインの条件を満たしていません';

const expectedAuthenticationErrorDefinitions = {
  missing_token: {
    id: 'auth.authentication.missingToken',
    httpStatus: 401,
    message: UnauthorizedMessage,
  },
  invalid_authorization_header: {
    id: 'auth.authentication.invalidAuthorizationHeader',
    httpStatus: 401,
    message: UnauthorizedMessage,
  },
  invalid_csrf_token: {
    id: 'auth.authentication.invalidCsrfToken',
    httpStatus: 403,
    message: ForbiddenMessage,
  },
  invalid_token: {
    id: 'auth.authentication.invalidToken',
    httpStatus: 401,
    message: UnauthorizedMessage,
  },
  expired_token: {
    id: 'auth.authentication.expiredToken',
    httpStatus: 401,
    message: UnauthorizedMessage,
  },
  revoked_token: {
    id: 'auth.authentication.revokedToken',
    httpStatus: 401,
    message: UnauthorizedMessage,
  },
  invalidated_session: {
    id: 'auth.authentication.invalidatedSession',
    httpStatus: 401,
    message: UnauthorizedMessage,
  },
  idle_session_expired: {
    id: 'auth.authentication.idleSessionExpired',
    httpStatus: 401,
    message: UnauthorizedMessage,
  },
  unverified_email: {
    id: 'auth.authentication.unverifiedEmail',
    httpStatus: 403,
    message: EmailNotVerifiedMessage,
  },
  account_not_found: {
    id: 'auth.authentication.accountNotFound',
    httpStatus: 401,
    message: UnauthorizedMessage,
  },
  no_accessible_projects: {
    id: 'auth.authentication.noAccessibleProjects',
    httpStatus: 403,
    message: NoAccessibleProjectsMessage,
  },
  saml_entry_denied: {
    id: 'auth.authentication.samlEntryDenied',
    httpStatus: 403,
    message: SamlEntryDeniedMessage,
  },
} satisfies Record<ExpectedAuthenticationErrorReason, ApplicationErrorCode>;

type ExpectedAuthenticationErrorDefinitions = typeof expectedAuthenticationErrorDefinitions;
export type ExpectedAuthenticationErrorCode = ExpectedAuthenticationErrorDefinitions[ExpectedAuthenticationErrorReason];
export type ExpectedAuthenticationErrorResponse = ApplicationErrorResponse<ExpectedAuthenticationErrorCode>;

export type ExpectedAuthenticationErrorLike = ApplicationErrorLike<ExpectedAuthenticationErrorCode> & {
  reason: ExpectedAuthenticationErrorReason;
};

const expectedAuthenticationErrorReasonsById = Object.fromEntries(
  Object.entries(expectedAuthenticationErrorDefinitions).map(([reason, errorCode]) => [errorCode.id, reason]),
) as Record<ExpectedAuthenticationErrorCode['id'], ExpectedAuthenticationErrorReason>;

export class ExpectedAuthenticationError extends ApplicationError<ExpectedAuthenticationErrorCode> {
  static Name = 'ExpectedAuthenticationError';

  readonly reason: ExpectedAuthenticationErrorReason;

  constructor(reason: ExpectedAuthenticationErrorReason, options?: { cause?: unknown }) {
    const errorCode = expectedAuthenticationErrorDefinitions[reason];
    super(errorCode, undefined, options);
    this.name = ExpectedAuthenticationError.Name;
    this.reason = reason;
  }
}

const toRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  return value as Record<string, unknown>;
};

const getExpectedAuthenticationErrorReason = (errorCodeId: unknown): ExpectedAuthenticationErrorReason | undefined => {
  if (typeof errorCodeId !== 'string') return undefined;
  return expectedAuthenticationErrorReasonsById[errorCodeId as ExpectedAuthenticationErrorCode['id']];
};

export const isExpectedAuthenticationError = (error: unknown): error is ExpectedAuthenticationErrorLike => {
  if (error instanceof ExpectedAuthenticationError) return true;
  const record = toRecord(error);
  const errorCode = toRecord(record?.errorCode);
  const reason = getExpectedAuthenticationErrorReason(errorCode?.id);
  return (
    record?.name === ExpectedAuthenticationError.Name &&
    reason !== undefined &&
    record.reason === reason &&
    errorCode?.httpStatus === expectedAuthenticationErrorDefinitions[reason].httpStatus &&
    errorCode.message === expectedAuthenticationErrorDefinitions[reason].message
  );
};

export const toExpectedAuthenticationErrorResponse = (error: ExpectedAuthenticationErrorLike): ExpectedAuthenticationErrorResponse =>
  toApplicationErrorResponse(error);

const getErrorCode = (error: unknown): string | undefined => {
  const record = toRecord(error);
  if (!record) return undefined;
  if (typeof record.code === 'string') return record.code;

  const errorInfo = toRecord(record.errorInfo);
  if (typeof errorInfo?.code === 'string') return errorInfo.code;
  return undefined;
};

export const toExpectedFirebaseAuthenticationError = (error: unknown): ExpectedAuthenticationError | undefined => {
  switch (getErrorCode(error)) {
    case 'auth/id-token-revoked':
    case 'auth/session-cookie-revoked':
      return new ExpectedAuthenticationError('revoked_token', { cause: error });
    case 'auth/id-token-expired':
    case 'auth/session-cookie-expired':
      return new ExpectedAuthenticationError('expired_token', { cause: error });
    case 'auth/argument-error':
    case 'auth/invalid-id-token':
    case 'auth/invalid-session-cookie':
      return new ExpectedAuthenticationError('invalid_token', { cause: error });
    default:
      return undefined;
  }
};
